import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Crosshair } from 'react-feather';
import { apiPost, apiError } from 'Shared/api';
import { Button } from 'Shared/UI';
import { useAuth, normalizeUserType } from 'Utils/Hooks/useAuth';
import { SSO_PATH, isHandoffUrl } from './helpers';

export * from './helpers';

/** True for operations staff (ADMIN / SUPPER_ADMIN); the endpoint refuses everyone else. */
export const canUseLogTracer = (user) => normalizeUserType(user?.user_type) === 'ADMIN';

/**
 * Opens the log tracer in a new tab, signed in through the portal.
 *
 * The tab is opened synchronously in the click (so pop-up blockers allow it), shows
 * a short note while the link is minted, then goes to the tracer. It has no opener
 * (the tracer cannot reach back into the portal). On failure the tab is closed and a
 * toast explains why.
 */
export const useLogTracer = () => {
  const [busy, setBusy] = useState(false);
  const open = async (search) => {
    const tab = window.open('', '_blank');
    if (tab) {
      try {
        tab.opener = null;
        tab.document.title = 'Log tracer';
        tab.document.body.textContent = 'Signing you in to the log tracer…';
      } catch (e) {
        /* cross-origin or closed: fine */
      }
    }
    setBusy(true);
    try {
      const res = await apiPost(SSO_PATH, search || {});
      const url = res?.data?.handoff_url;
      if (!url || !isHandoffUrl(url)) throw new Error('The server did not return a log tracer sign-in link.');
      if (tab && !tab.closed) tab.location.replace(url);
      else if (!window.open(url, '_blank', 'noopener')) throw new Error('Your browser blocked the new tab. Allow pop-ups for this site and try again.');
    } catch (e) {
      if (tab && !tab.closed) tab.close();
      toast.error(apiError(e, 'Could not open the log tracer.'));
    } finally {
      setBusy(false);
    }
  };
  return [open, busy];
};

/**
 * "Trace this": opens the tracer with a search filled in. Renders nothing for anyone
 * but operations staff, so shared (partner + admin) pages can include it safely.
 */
export const TraceButton = ({ search, children = 'Trace this', title, variant = 'ghost', size = 'sm', stopPropagation = true, className = '' }) => {
  const { currentAdmin } = useAuth();
  const [open, busy] = useLogTracer();
  if (!canUseLogTracer(currentAdmin)) return null;
  const label = title || 'Open in the log tracer (new tab)';
  return (
    <Button
      variant={variant}
      size={size}
      icon={Crosshair}
      busy={busy}
      title={label}
      aria-label={typeof children === 'string' ? `${children}: ${label}` : label}
      className={`lt-trace ${className}`}
      onClick={(e) => {
        if (stopPropagation && e) e.stopPropagation();
        open(typeof search === 'function' ? search() : search);
      }}
    >
      {children}
    </Button>
  );
};
